import * as React from 'react'
import { flushSync } from 'react-dom'

import { selectionKey } from '../utils'
import { backOf, type SheetView } from './view'

/**
 * View steps that unmount the focused control, each with where focus lands.
 * flushSync puts the target on screen before it is focused.
 */
export function useSheetNavigation(
  view: SheetView,
  onViewChange: (view: SheetView) => void,
  /** False when the open row left the list, which then shows as the root. */
  rowResolved: boolean
) {
  const sheetRef = React.useRef<HTMLDivElement>(null)
  const backRef = React.useRef<HTMLButtonElement>(null)
  const contentToggleRef = React.useRef<HTMLDivElement>(null)

  const focusContentToggle = () =>
    contentToggleRef.current
      ?.querySelector<HTMLElement>('[data-state="on"]')
      ?.focus()
  const previous = view.tab === 'content' || rowResolved ? backOf(view) : null

  return {
    sheetRef,
    backRef,
    contentToggleRef,
    canGoBack: previous !== null,
    drillInto: (next: SheetView) => {
      flushSync(() => onViewChange(next))
      backRef.current?.focus()
    },
    showCompare: (compare: boolean) => {
      flushSync(() => onViewChange({ tab: 'content', compare }))
      focusContentToggle()
    },
    leaveCompare: (change: () => void) => {
      flushSync(change)
      focusContentToggle()
    },
    back: () => {
      if (!previous) return
      flushSync(() => onViewChange(previous))
      if (view.tab === 'content') {
        focusContentToggle()
      } else if (view.open) {
        sheetRef.current
          ?.querySelector<HTMLElement>(
            `[data-row-key="${selectionKey(view.open)}"]`
          )
          ?.focus()
      }
    },
  }
}
